<script setup lang="ts">
import type { ProductOptionSelector } from '#components'
import type { OrderLineFragment, SearchProductVariantResultFragment } from '#graphql-operations'
import type { ComponentExposed } from 'vue-component-type-helpers'
import * as Sentry from '@sentry/nuxt'

const props = defineProps<{
  productId: string | number
}>()

const open = defineModel<boolean>('open', {
  default: false,
  type: Boolean,
})

const { ecommerce } = useAnalytics()
const { t } = useI18n()
const { isMobile } = useDevice()
const priceFormatter = usePriceFormatter()
const { toast } = useToast()

const activeOrderStore = useActiveOrderStore()
const uiStore = useUiStore()
const productStore = useProductStore()

const addingToCart = ref(false)
const showOptionErrors = ref(false)
const productOptionSelector = ref<ComponentExposed<typeof ProductOptionSelector>>()
const { selectedVariantImageIndex, variantImage } = useProductVariantImage()

// Function to load product data
async function loadProduct(id: string | number) {
  productStore.reset()
  await nextTick()
  await productStore.fetchProduct(id)
  productStore.initializeFromQuery()
}

// Load product when dialog opens
watch(open, async (open) => {
  if (open && props.productId) {
    await loadProduct(props.productId)
  }
}, { immediate: true })

// Clean up when dialog closes
watch(open, (open) => {
  if (!open) {
    productStore.reset()
  }
})

async function handleAddToCart(selectedVariant?: SearchProductVariantResultFragment, quantity = 1) {
  if (productStore.status === 'pending' || addingToCart.value)
    return

  if (!productStore.allOptionsSelected) {
    showOptionErrors.value = true
    productOptionSelector.value?.wiggleUnselectedOptionGroups?.()
    toast({
      title: t('product.error.please_select_all_options'),
      variant: 'destructive',
    })
    return
  }

  if (!selectedVariant) {
    toast({
      title: t('product.error.variant_not_found'),
      variant: 'destructive',
    })
    return
  }

  addingToCart.value = true

  try {
    await activeOrderStore.addItemToOrder(selectedVariant, quantity)
    ecommerce.addToCart(selectedVariant, quantity)

    // Close quick view and open cart
    open.value = false
    uiStore.openCartDrawer()
    ecommerce.viewCart()
  }
  catch (error) {
    Sentry.captureException(error)
    if (error instanceof Error)
      toast({ title: error.message, variant: 'destructive' })
  }
  finally {
    addingToCart.value = false
  }
}

async function handleAdjustOrderLine(orderLine: OrderLineFragment, quantity: number) {
  if (productStore.status === 'pending' || addingToCart.value)
    return

  addingToCart.value = true
  await activeOrderStore.adjustOrderLine(orderLine, quantity)
  addingToCart.value = false

  if (quantity > orderLine.quantity) {
    ecommerce.addToCart(orderLine, quantity)
    // Close quick view and open cart
    open.value = false
    uiStore.openCartDrawer()
    ecommerce.viewCart()
  }
  else {
    ecommerce.removeFromCart(orderLine, quantity)
  }
}

const currentImageIndex = ref(0)
</script>

<template>
  <!-- Mobile: Bottom Sheet -->
  <UiDrawer v-if="isMobile" v-model:open="open">
    <UiDrawerContent>
      <template v-if="productStore.product">
        <div class="w-full flex items-center justify-between border-b n-border-base px4 py3">
          <div class="flex items-center">
            <NuxtImg
              :src="variantImage"
              :alt="productStore.product.name"
              class="h16 w16"
              width="100"
              height="100"
              loading="lazy"
            />
            <div class="ml2">
              <div class="line-clamp-1 overflow-hidden text-context" n="sm slate5 dak:slate4">
                {{ productStore.product.name }}
              </div>

              <div
                v-if="productStore.selectedVariant?.currency && productStore.selectedVariant?.price"
                class="flex items-center justify-start space-x-2"
              >
                <span class="text-error font-bold" n="xl">
                  {{ priceFormatter(productStore.price, productStore.selectedVariant.currency) }}
                </span>
                <template v-if="productStore?.priceBeforeDiscount && productStore.priceBeforeDiscount > productStore.selectedVariant.price">
                  <span class="text-context line-through" n="slate5 lg">
                    {{ priceFormatter(productStore.priceBeforeDiscount, productStore.selectedVariant.currency) }}
                  </span>
                  <UiBadge v-if="productStore.discountPercentage" variant="error" class="rounded-md px-2 py-1 fw600 tracking-wider">
                    -{{ productStore.discountPercentage }}%
                  </UiBadge>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div class="mb8 px4 pb2">
            <ProductOptionSelector
              ref="productOptionSelector"
              v-model:show-option-errors="showOptionErrors"
            />
          </div>
        </div>

        <div class="pb-safe">
          <div class="p4 pb6">
            <AddToCart
              class="mb:pb0 py5 pb2"
              :loading="addingToCart"
              @add-to-cart="handleAddToCart(productStore.activeVariant)"
              @change:order-line="handleAdjustOrderLine"
            />
          </div>
        </div>
      </template>

      <div v-else class="p-4">
        <div class="mb-4 flex items-center space-x-4">
          <NSkeleton class="h-16 w-16 rounded" shape="rect" />
          <div class="flex-1">
            <NSkeleton class="mb-2 h-4 w-3/4" />
            <NSkeleton class="h-6 w-24" />
          </div>
        </div>
        <div class="space-y-4">
          <NSkeleton class="h-12 w-full" />
          <NSkeleton class="h-12 w-full" />
          <NSkeleton class="h-14 w-full rounded" shape="rect" />
        </div>
      </div>
    </UiDrawerContent>
  </UiDrawer>

  <!-- Desktop: Dialog -->
  <UiDialog v-else v-model:open="open">
    <UiDialogScrollContent class="max-w-4xl">
      <template v-if="productStore.product">
        <div class="grid grid-cols-2 gap-6">
          <!-- Gallery -->
          <div>
            <ProductGallery
              :assets="productStore.images || []"
              :asset-index="selectedVariantImageIndex"
              @update:asset-index="currentImageIndex = $event"
            />
          </div>

          <!-- Product Info -->
          <div class="flex flex-col">
            <UiDialogHeader>
              <UiDialogTitle class="text-xl">
                {{ productStore.product.name }}
              </UiDialogTitle>
            </UiDialogHeader>

            <div class="flex-1">
              <!-- Price -->
              <div class="mt-4">
                <div class="flex items-center justify-start space-x-2">
                  <span class="text-2xl text-error font-bold">
                    {{ priceFormatter(productStore?.price, productStore.product?.currency) }}
                  </span>
                  <template v-if="productStore?.priceBeforeDiscount && productStore.priceBeforeDiscount > productStore?.price">
                    <span class="text-xl text-black line-through">
                      {{ priceFormatter(productStore.priceBeforeDiscount, productStore.product?.currency) }}
                    </span>
                    <UiBadge v-if="productStore.discountPercentage" variant="error" class="rounded-md px-2 py-1 text-sm fw600">
                      -{{ productStore.discountPercentage }}%
                    </UiBadge>
                  </template>
                </div>
              </div>

              <!-- Stock status -->
              <div class="mt-4 flex items-center">
                <span class="relative size-3 flex">
                  <span class="absolute h-full w-full inline-flex animate-[ping_1s_infinite_1500ms] rounded-full bg-success opacity-75" />
                  <span class="relative size-3 inline-flex rounded-full bg-success" />
                </span>
                <span class="ml-2 text-success font-bold">
                  {{ t('product.in_stock') }}, {{ t('product.ships_immediately') }}
                </span>
              </div>

              <!-- Options -->
              <div class="mt-6">
                <ProductOptionSelector
                  ref="productOptionSelector"
                  v-model:show-option-errors="showOptionErrors"
                />
              </div>

              <!-- Add to cart -->
              <div class="mt-6">
                <AddToCart
                  class="mb:pb0 py5 pb2"
                  :loading="addingToCart"
                  @add-to-cart="handleAddToCart(productStore.activeVariant)"
                  @change:order-line="handleAdjustOrderLine"
                />
              </div>

              <!-- View full details link -->
              <div class="mt-4 text-center">
                <NuxtLinkLocale
                  :to="`/products/${productId}/${productStore.product.slug}`"
                  class="text-sm text-primary hover:underline"
                  @click="open = false"
                >
                  {{ t('product.view_full_details') }}
                </NuxtLinkLocale>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="p-6">
        <div class="grid grid-cols-2 gap-6">
          <!-- Gallery skeleton -->
          <div>
            <NSkeleton class="aspect-square w-full rounded" shape="rect" />
          </div>
          <!-- Info skeleton -->
          <div class="space-y-4">
            <NSkeleton class="h-8 w-3/4" />
            <NSkeleton class="h-10 w-32" />
            <NSkeleton class="h-4 w-full" />
            <div class="mt-6 space-y-2">
              <NSkeleton class="h-12 w-full" />
              <NSkeleton class="h-12 w-full" />
            </div>
            <NSkeleton class="mt-6 h-14 w-full rounded" shape="rect" />
          </div>
        </div>
      </div>
    </UiDialogScrollContent>
  </UiDialog>
</template>
